import React, { useRef, useCallback, useEffect } from 'react';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LinkIcon from '@mui/icons-material/Link';

const RichTextEditor = ({ value = '', onChange, label, minHeight = 200 }) => {
    const editorRef = useRef(null);
    // Track whether the editor is currently focused to avoid overwriting user input
    const isFocused = useRef(false);

    // Set initial HTML only on mount, and when value changes externally (not from user typing)
    useEffect(() => {
        if (editorRef.current && !isFocused.current) {
            editorRef.current.innerHTML = value;
        }
    }, [value]);

    const exec = useCallback((command, val = null) => {
        document.execCommand(command, false, val);
        editorRef.current?.focus();
        if (onChange && editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    }, [onChange]);

    const handleInput = () => {
        if (onChange && editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const addLink = () => {
        const url = window.prompt('Enter URL');
        if (url) exec('createLink', url);
    };

    return (
        <Box>
            {label && (
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {label}
                </Typography>
            )}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderBottom: '1px solid #e0e0e0', bgcolor: '#f9f9f9' }}>
                    <IconButton size="small" onClick={() => exec('bold')} title="Bold">
                        <FormatBoldIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => exec('italic')} title="Italic">
                        <FormatItalicIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => exec('underline')} title="Underline">
                        <FormatUnderlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => exec('insertUnorderedList')} title="Bullet list">
                        <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={addLink} title="Insert link">
                        <LinkIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onFocus={() => { isFocused.current = true; }}
                    onBlur={() => { isFocused.current = false; }}
                    onInput={handleInput}
                    sx={{
                        minHeight,
                        p: 2,
                        outline: 'none',
                        fontSize: '14px',
                        lineHeight: 1.6,
                        direction: 'ltr',
                        textAlign: 'left',
                        '&:empty:before': {
                            content: '"Write email body here..."',
                            color: '#999',
                        },
                    }}
                />
            </Paper>
        </Box>
    );
};

export default RichTextEditor;
